import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import type {
  PackagingItemPackagedItemLink,
  PackagingSupplierPackagedItem,
} from '@/shared/hooks/api/packagingSuppliers'
import { useUpdatePackagingItem } from '@/shared/hooks/api/packagingSuppliers'

type LexAssignPackagingToPartSheetProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  supplierId: string
  packagingItemId: string
  linkedParts: PackagingItemPackagedItemLink[]
  packagedItems: PackagingSupplierPackagedItem[]
}

export function LexAssignPackagingToPartSheet({
  open,
  onOpenChange,
  supplierId,
  packagingItemId,
  linkedParts,
  packagedItems,
}: LexAssignPackagingToPartSheetProps) {
  const { t } = useTranslation('packagingSuppliers')
  const updateItem = useUpdatePackagingItem(supplierId, packagingItemId)

  const linkedPartIds = useMemo(() => {
    return new Set(linkedParts.map((part) => part.id))
  }, [linkedParts])

  const availableParts = useMemo(() => {
    return packagedItems.filter((part) => !linkedPartIds.has(part.id))
  }, [linkedPartIds, packagedItems])

  const emptyPartsMessage =
    packagedItems.length === 0
      ? t('details.yourPackagingItems.assignToPartDialogEmptyNoParts')
      : t('details.yourPackagingItems.assignToPartDialogEmptyAllPartsLinked')

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side='right'
        className='flex h-full w-full flex-col gap-0 overflow-hidden p-0 sm:max-w-md'
      >
        <div className='flex min-h-0 flex-1 flex-col'>
          <SheetHeader className='shrink-0 border-b px-6 py-4 text-start'>
            <SheetTitle>
              {t('details.yourPackagingItems.assignToPartDialogTitle')}
            </SheetTitle>
          </SheetHeader>
          <div className='flex-1 space-y-2 overflow-y-auto px-6 py-4'>
            {availableParts.length === 0 ? (
              <p className='text-sm text-muted-foreground'>{emptyPartsMessage}</p>
            ) : (
              availableParts.map((part) => (
                <button
                  key={part.id}
                  type='button'
                  className='flex w-full flex-col rounded-md border px-3 py-2 text-left text-sm hover:bg-muted/50'
                  disabled={updateItem.isPending}
                  onClick={() => {
                    void (async () => {
                      const existingIds = linkedParts.map((link) => link.id)
                      await updateItem.mutateAsync({
                        packagesOwnProduct: true,
                        packagedItemIds: [...existingIds, part.id],
                      })
                      onOpenChange(false)
                    })()
                  }}
                >
                  <span className='font-medium'>{part.name}</span>
                  <span className='text-muted-foreground'>
                    {part.sku?.trim() || t('common:notAvailable')}
                  </span>
                </button>
              ))
            )}
          </div>
          <SheetFooter className='shrink-0 border-t px-6 py-4 sm:flex-row sm:justify-end'>
            <Button
              type='button'
              variant='outline'
              onClick={() => onOpenChange(false)}
            >
              {t('common:actions.cancel')}
            </Button>
          </SheetFooter>
        </div>
      </SheetContent>
    </Sheet>
  )
}
